// główny layout - mobilne menu dolne
// @ts-nocheck

import React, { useCallback, useState } from 'react';
import { FileEarmarkText, Person, List, X, BoxArrowRight } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames';

import { reduxActions, useDispatch, useSelector } from 'store';
import { useAppNavigate } from 'hooks';
import { useGetUsersTrader, usePostOverLogout } from 'api';
import { Modal } from 'components/controls';

import { CartsButton, MobileMenu, DashboardMenu } from 'components/containers';
import { userRolesAccessRoutes } from 'components/layouts/DashboardSubLayout/SubLayoutDashboard';
import { ReactComponent as HeadsetIcon } from 'assets/icons/headset.svg';

import styles from 'theme/components/layouts/MainLayout/components/BottomMenu/BottomMenu.module.scss';

const BottomMenu = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { profile, overloginUserId } = useSelector((state) => state.auth);
  const { isMobileMenu } = useSelector((state) => state.ui);
  const navigate = useAppNavigate();
  const { pathname } = useLocation();

  // pobranie opiekuna
  const { data: salesmanData } = useGetUsersTrader();
  const { mutateAsync: postOverLogout } = usePostOverLogout();

  const handleLogout = useCallback(async () => {
    setIsAccountMenuOpen(false);
    if (overloginUserId) {
      navigate('/', { replace: true });
      try {
        await postOverLogout();
      } catch {
        // ignorujemy błąd API – i tak czyścimy overlogin lokalnie
      } finally {
        dispatch(reduxActions.setCurrentCartId(null));
        dispatch(reduxActions.setOverlogin(null));
      }
      return;
    }
    dispatch(reduxActions.signOut());
  }, [dispatch, navigate, overloginUserId, postOverLogout]);

  // czy jest otwarty modal kontaktu
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  // czy jest otwarte menu Konto (nawigacja dashboard)
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  // TODO wydzielic w inne miejsce i usunąć duplikaty
  const isCurrentPath = useCallback(
    (testedPath?: string) =>
      testedPath ? new RegExp(`^/[^\\/]+${testedPath}$`).test(pathname) : false,
    [pathname]
  );

  // Funkcja do otwarcia modala kontaktu
  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  const items =
    profile?.role !== 'ROLE_OPEN_PROFILE'
      ? [
          // 1. Listy
          {
            icon: <FileEarmarkText />,
            label: <Trans>Listy</Trans>,
            onClick: () => {
              navigate('/dashboard/shopping-lists');
            },
            active: isCurrentPath('/dashboard/shopping-lists')
          },
          // 2. Konto – otwiera overlay z menu dashboard (Lista płatności, zamówień, itd.)
          {
            icon: <Person />,
            label: <Trans>Konto</Trans>,
            onClick: () => setIsAccountMenuOpen(true),
            active: isCurrentPath('/dashboard')
          },
          // 3. Hamburger Menu (wystający za pasek)
          {
            icon: (
              <span className={styles.hamburgerIcon}>
                <List />
              </span>
            ),
            label: null,
            onClick: () => {
              dispatch(reduxActions.setIsMobileMenu(!isMobileMenu));
            },
            isHamburger: true
          },
          // 4. Koszyk
          {
            icon: <CartsButton />,
            label: <Trans>Koszyk</Trans>
          },
          // 5. Kontakt
          {
            icon: <HeadsetIcon />,
            label: <Trans>Kontakt</Trans>,
            onClick: handleContactClick
          }
        ]
      : [
          {
            icon: <MobileMenu />,
            label: <Trans>Produkty</Trans>,
            onClick: () => {
              dispatch(reduxActions.setIsMobileMenu(false));
              navigate('/products?return_url=/');
            },
            active: isCurrentPath('/products')
          },
          {
            icon: (
              <span className={styles.hamburgerIcon}>
                <List />
              </span>
            ),
            label: <Trans>Kategorie</Trans>,
            onClick: () => dispatch(reduxActions.setIsMobileMenu(!isMobileMenu)),
            isHamburger: true
          },
          {
            icon: <Person />,
            label: <Trans>Logowanie</Trans>,
            onClick: () => {
              navigate('/login?return_url=/');
            }
          }
        ];

  return (
    <>
      <div
        className={classnames(
          styles.componentWrapper,
          'StylePath-Components-Layouts-MainLayout-Components-BottomMenu'
        )}>
        {items.map((item, index) => (
          <div
            key={index}
            className={classnames(
              styles.menuItem,
              { [styles.active]: item.active },
              { [styles.hamburgerMenuItem]: item.isHamburger }
            )}
            onClick={item.onClick}>
            {item.icon}
            {item.label && item.label}
          </div>
        ))}
      </div>

      {/* Overlay menu Konto – nawigacja dashboard (po kliknięciu w przycisk Konto) */}
      {isAccountMenuOpen && profile?.role && (
        <div className={styles.accountMenuOverlay}>
          <div className={styles.topBox}>
            <span className={styles.label}>
              <Trans>Zalogowany jako</Trans>:
            </span>
            <span className={styles.name}>
              {profile?.first_name} {profile?.last_name}
            </span>
            <span className={styles.company}>{profile?.customer?.name}</span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setIsAccountMenuOpen(false)}
              aria-label={undefined}>
              <X />
            </button>
          </div>
          <div className={styles.menuBox}>
            <DashboardMenu
              accessRoutes={userRolesAccessRoutes()[profile.role] || {}}
              onLinkClick={() => setIsAccountMenuOpen(false)}
            />
          </div>
          <div className={styles.logoutBox}>
            <button
              type="button"
              className={styles.logoutButton}
              onClick={handleLogout}
              aria-label={overloginUserId ? t('Wróć na konto') : t('Wyloguj')}>
              <BoxArrowRight className={styles.logoutIcon} />
              {overloginUserId ? t('Wróć na konto') : t('Wyloguj')}
            </button>
          </div>
        </div>
      )}

      {/* Modal kontaktu */}
      {isContactModalOpen && (
        <Modal title={<Trans>Kontakt</Trans>} onClose={() => setIsContactModalOpen(false)}>
          {!Array.isArray(salesmanData) && salesmanData && (
            <div className={styles.modalContent}>
              <HeadsetIcon className={styles.modalIcon} />
              <div className={styles.modalText}>
                <span>
                  {salesmanData.first_name} {salesmanData.last_name}
                  <br />
                  <Trans>Telefon</Trans>: <strong>{salesmanData.phone}</strong>
                  <br />
                  <Trans>Email</Trans>: <strong>{salesmanData.email}</strong>
                </span>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
};

export default BottomMenu;
